// Outbox CRUD — local-first persistence (spec §8). Every commit is written here
// BEFORE any network call, so a connection drop never loses a sale.

import { type CommitPayload, type OutboxItem, type OutboxStatus, openOperatorDB } from "./db";

/** Write a commit to the outbox before any network call. Idempotent by
 *  tx_uuid; never clobbers an already-synced item (its official number stands). */
export async function enqueue(payload: CommitPayload, enqueuedAt: string): Promise<OutboxItem> {
  const db = await openOperatorDB();
  const existing = await db.get("outbox", payload.tx_uuid);
  if (existing?.status === "synced") {
    return existing;
  }
  const item: OutboxItem = existing
    ? { ...existing, payload }
    : {
        tx_uuid: payload.tx_uuid,
        payload,
        captured_at: payload.captured_at,
        status: "pending",
        enqueued_at: enqueuedAt,
      };
  await db.put("outbox", item);
  return item;
}

/** Items eligible for an automatic drain attempt, in FIFO capture order. Only
 *  `pending`: a `conflict` is a server HARD rejection (deterministic), so
 *  re-POSTing it just re-rejects — it is surfaced for manual resolution
 *  (conflictCount) rather than retried in a loop. `synced` is done. */
export async function pendingForDrain(): Promise<OutboxItem[]> {
  const db = await openOperatorDB();
  const all = await db.getAllFromIndex("outbox", "by_captured_at");
  return all.filter((i) => i.status === "pending");
}

/** All items in capture order — for the recent/reprint/reconciliation views. */
export async function allItems(): Promise<OutboxItem[]> {
  const db = await openOperatorDB();
  return db.getAllFromIndex("outbox", "by_captured_at");
}

export async function getItem(txUuid: string): Promise<OutboxItem | undefined> {
  const db = await openOperatorDB();
  return db.get("outbox", txUuid);
}

export async function patchItem(txUuid: string, patch: Partial<OutboxItem>): Promise<void> {
  const db = await openOperatorDB();
  const item = await db.get("outbox", txUuid);
  if (!item) {
    return;
  }
  await db.put("outbox", { ...item, ...patch });
}

/** Drop an un-synced item (in-window undo of an offline sale): nothing reached
 *  the server, so no invoice number was consumed (§8.7 path 1). A `synced` item
 *  is NOT deletable here — it must go through the server void path. */
export async function deleteIfUnsynced(txUuid: string): Promise<boolean> {
  const db = await openOperatorDB();
  const item = await db.get("outbox", txUuid);
  if (!item || item.status === "synced") {
    return false;
  }
  await db.delete("outbox", txUuid);
  return true;
}

/** Count of items still owing a successful drain — for the header sync badge. */
export async function pendingCount(): Promise<number> {
  return (await pendingForDrain()).length;
}

export async function countByStatus(status: OutboxStatus): Promise<number> {
  const db = await openOperatorDB();
  return db.countFromIndex("outbox", "by_status", status);
}

/** Items that a server HARD-rejected at drain (e.g. tamper/integrity) and need
 *  manual resolution — surfaced distinctly so they never sit as silent dead
 *  money, and never inflate the normal "to sync" count. */
export async function conflictCount(): Promise<number> {
  return countByStatus("conflict");
}
